import { Tooltip } from "@mui/material";
import type { MouseEvent } from "react";
import type { TFunction } from "i18next";
import type { AppDispatch } from "src/common/state/store";
import { Checkbox } from "src/common/components/Input";
import RoundImagePreview from "src/features/projects/components/RoundImagePreview";
import { fetchUserDetails, setDeleteUsersDraft } from "src/features/team/state/teamSlice";
import type { getEndusers } from "src/features/team/state/teamSlice";
import { openConfirmationModal } from "src/common/state/slice/modal/modalSlice";
import { CREATE_OR_UPDATE_USER_AS_ADMIN_PERMISSIONS_LIST_COMPONENT } from "src/common/components/CreateOrUpdateUserAsAdmin/CreateOrUpdateUserAsAdminPermissionsList";
import type { IAllowedItem } from "./DisplayAllowedItems";
import { DisplayAllowedItems } from "./DisplayAllowedItems";
import { UserCrudButtons } from "./UserCrudButtons";
import SimplifiedUserIcon from "../../../assets/simplified-user-round-marker.png";

export type EnduserForRow = ReturnType<typeof getEndusers>[number];

export interface BuildEnduserRowParams {
  enduser: EnduserForRow;
  index: number;
  isChecked: boolean;
  enduserIsSimplified: boolean;
  hasAccessToEverything: boolean;
  allowedItems: IAllowedItem[];
  t: TFunction;
  dispatch: AppDispatch;
  onCheckboxClick: (e: MouseEvent<HTMLInputElement>) => void;
  onEditNavigate: () => void;
}

export const buildEnduserRow = ({
  enduser,
  index,
  isChecked,
  enduserIsSimplified,
  hasAccessToEverything,
  allowedItems,
  t,
  dispatch,
  onCheckboxClick,
  onEditNavigate,
}: BuildEnduserRowParams) => ({
  checkbox: (
    <Checkbox
      checked={isChecked}
      key={`${index}-${enduser.id}`}
      id={`${enduser.id}`}
      value={Number(enduser.id)}
      onClick={(e: MouseEvent<HTMLInputElement>) => {
        e.stopPropagation();
        onCheckboxClick(e);
      }}
    />
  ),
  avatar: <RoundImagePreview mediaId_OrIsItAnObjectLol={enduser.source_avatar} />,
  firstName: !enduserIsSimplified ? (
    enduser.firstname
  ) : (
    <div className="flex relative">
      <Tooltip title={t("general.userWithSimplifiedLogin")}>
        <div className="absolute -left-8 top-[2px] flex items-center justify-center rounded-full bg-blue-500 text-white">
          <img src={SimplifiedUserIcon} className="w-4 h-4" />
        </div>
      </Tooltip>
      <span> {enduser.firstname}</span>
    </div>
  ),
  lastName: enduser.lastname,
  email: enduser.email,
  projectGroups: (
    <DisplayAllowedItems
      itemsAreProjectGroups={false}
      hasAccessToEverything={hasAccessToEverything}
      allowedItems={allowedItems}
    />
  ),
  crudButtons: (
    <UserCrudButtons
      editDisabled={enduserIsSimplified}
      editDisabledTooltip={t("pages.participants.simplifiedUsersCannotBeEdited")}
      onClickEdit={() => {
        void dispatch(
          fetchUserDetails({
            ...enduser,
            componentId: CREATE_OR_UPDATE_USER_AS_ADMIN_PERMISSIONS_LIST_COMPONENT,
          }),
        );
        onEditNavigate();
      }}
      onClickDelete={() => {
        dispatch(
          setDeleteUsersDraft({
            type: "delete_enduser",
            usersToBeDeleted: [{ id: enduser.id, email: enduser.email }],
          }),
        );
        dispatch(openConfirmationModal("confirm-delete-users"));
      }}
    />
  ),
});
